import axios from 'axios';
import type { AxiosError, AxiosInstance } from 'axios';
import { traceResolvedError } from './devTrace';
import { resolveApiError } from './resolveApiError';
import { reportApiError, shouldReport } from './reporting';
import type { UnifiedErrorEnvelope } from './types';

declare module 'axios' {
	interface AxiosRequestConfig {
		// Statuses this caller treats as an expected outcome, never a bug — they skip
		// Sentry even when `shouldReport` would report them (e.g. a contract violation).
		silentStatuses?: readonly number[];
	}
}

/** Key used to tag an AxiosError with its resolution, without replacing the error itself. */
export const UNIFIED_ERROR_PROP = '__unifiedError' as const;

/** Reads the envelope tagged by `attachErrorReporting`, if any. Never throws. */
export const readUnifiedError = (error: unknown): UnifiedErrorEnvelope | undefined => {
	if (typeof error !== 'object' || error === null) return undefined;
	if (!(UNIFIED_ERROR_PROP in error)) return undefined;
	return (error as Record<typeof UNIFIED_ERROR_PROP, UnifiedErrorEnvelope>)[UNIFIED_ERROR_PROP];
};

/** Tags `error` with `envelope` in place. No-op if already tagged (redefining would throw). */
const attachUnified = (error: AxiosError, envelope: UnifiedErrorEnvelope): void => {
	if (UNIFIED_ERROR_PROP in error) return;
	Object.defineProperty(error, UNIFIED_ERROR_PROP, {
		value: envelope,
		enumerable: false, // hidden from Object.keys/JSON.stringify/AxiosError.toJSON()
		writable: false,
		configurable: false,
	});
};

/**
 * Registers the error-reporting interceptor on `instance`. Must run AFTER the
 * auth/refresh interceptor — that ordering, not a flag, is what keeps reporting
 * once-only. Rejects the same AxiosError it received, never a new object, so code
 * reading `error.response?.status` directly (e.g. reactQuery's retry) sees no difference.
 */
export const attachErrorReporting = (instance: AxiosInstance): void => {
	instance.interceptors.response.use(
		(response) => response,
		(error: unknown) => {
			if (!axios.isAxiosError(error)) return Promise.reject(error);

			// Already resolved once (e.g. this interceptor saw the same error twice) — skip.
			const existing = readUnifiedError(error);
			if (existing) return Promise.reject(error);

			const resolved = resolveApiError(error);
			const status = error.response?.status;
			const silencedByCaller =
				status !== undefined && (error.config?.silentStatuses?.includes(status) ?? false);
			const reported = !silencedByCaller && shouldReport(resolved);
			if (reported) reportApiError(error, resolved);
			// DEV-only, once per intercepted error — NOT wired into `resolveApiError` itself,
			// which render-body consumers call on every render (ADR-9).
			traceResolvedError(resolved);

			attachUnified(error, { resolved, reported });

			return Promise.reject(error);
		},
	);
};
